import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { toast } from "sonner";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import type {
  PriceGroupSummary,
  PricingAssignment,
} from "@shared-core/data/api/pricing";
import { paths } from "@shared-core/routing/routes";
import { Button } from "../app/components/primitives/button";
import { Badge } from "../app/components/primitives/badge";
import { RegistryTile } from "../app/components/patterns/RegistryTile";
import { Plus, Trash2, UserPlus } from "lucide-react";

// Price groups index — the "what's live" registry. Lists every group with its
// scope; tiles open the editor. Customer assignment section lets CSRs bind a
// customer to a non-standard pricing group.

export function PriceGroupsPage() {
  const adapter = useDataAdapter();
  const navigate = useNavigate();
  const [groups, setGroups] = useState<PriceGroupSummary[]>([]);
  const [assignments, setAssignments] = useState<PricingAssignment[]>([]);
  // Add-assignment inline form state.
  const [addingAssignment, setAddingAssignment] = useState(false);
  const [newCustomerCode, setNewCustomerCode] = useState("");
  const [newPriceGroupId, setNewPriceGroupId] = useState("");
  const [savingAssignment, setSavingAssignment] = useState(false);

  useEffect(() => {
    adapter
      .listPriceGroups()
      .then(setGroups)
      .catch(() => {});
    adapter
      .listPricingAssignments()
      .then(setAssignments)
      .catch(() => {});
  }, [adapter]);

  // Both handlers send only the row they are changing. Posting the whole list
  // was how "remove" came to be a no-op that reported success: the write path is
  // an upsert, so a customer filtered out of the payload was simply not
  // mentioned, not removed. It also made every save a lost-update risk — two
  // managers with this page open would overwrite each other from stale state.
  async function handleRemoveAssignment(customerCode: string) {
    try {
      const saved = await adapter.removePricingAssignments([customerCode]);
      setAssignments(saved);
      toast.success(`${customerCode} moved back to Standard`);
    } catch (err) {
      toast.error(
        err instanceof Error
          ? `Failed to remove assignment: ${err.message}`
          : "Failed to remove assignment",
      );
    }
  }

  async function handleAddAssignment() {
    const code = newCustomerCode.trim().toUpperCase();
    const priceGroupId = newPriceGroupId.trim();
    if (!code || !priceGroupId) return;
    setSavingAssignment(true);
    try {
      const saved = await adapter.updatePricingAssignments([
        { customerCode: code, customerName: null, priceGroupId },
      ]);
      setAssignments(saved);
      setAddingAssignment(false);
      setNewCustomerCode("");
      setNewPriceGroupId("");
      toast.success(`${code} assigned to ${priceGroupId}`);
    } catch (err) {
      toast.error(
        err instanceof Error
          ? `Failed to save assignment: ${err.message}`
          : "Failed to save assignment",
      );
    } finally {
      setSavingAssignment(false);
    }
  }

  const nonDefaultGroups = groups.filter((g) => g.scope !== "central");

  return (
    <div className="space-y-8">
      {/* ── Header ── */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1>Price groups</h1>
          <p className="text-sm text-muted-foreground">
            The pricing policies in force. Standard is the shared default;
            customers can have their own.
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            size="sm"
            onClick={() =>
              toast.info("New price group — not wired in the prototype")
            }
          >
            <Plus className="size-4" /> New group
          </Button>
        </div>
      </div>

      {/* ── Groups ── */}
      <div className="space-y-3">
        {groups.map((g) => {
          const name = g.scope === "central" ? "Standard" : g.name;
          return (
            <RegistryTile
              key={g.id}
              title={name}
              scope={
                g.scope === "central"
                  ? "shared"
                  : { customerName: g.scope.customerName }
              }
              onOpen={() =>
                navigate(paths.settingsPricingEdit({ groupId: g.id }))
              }
            />
          );
        })}
      </div>

      {/* ── Customer assignments ── */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-base font-semibold">Customer assignments</h2>
            <p className="text-sm text-muted-foreground">
              Customers on a non-standard group. Everyone else uses Standard.
            </p>
          </div>
          <Button
            size="sm"
            variant="outline"
            onClick={() => setAddingAssignment(true)}
          >
            <UserPlus className="size-4" /> Assign customer
          </Button>
        </div>

        {assignments.length === 0 && !addingAssignment && (
          <p className="text-sm text-muted-foreground">
            No overrides — all customers use the Standard group.
          </p>
        )}

        {assignments.map((a) => {
          const groupName =
            groups.find((g) => g.id === a.priceGroupId)?.name ?? a.priceGroupId;
          return (
            <div
              key={a.customerCode}
              className="flex items-center justify-between rounded-lg border border-border bg-card px-4 py-3"
            >
              <div className="flex items-center gap-3">
                <span className="font-medium text-sm">{a.customerCode}</span>
                {a.customerName && (
                  <span className="text-sm text-muted-foreground">
                    {a.customerName}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-3">
                <Badge variant="secondary">{groupName}</Badge>
                <button
                  type="button"
                  className="text-muted-foreground hover:text-destructive transition-colors"
                  onClick={() => handleRemoveAssignment(a.customerCode)}
                  title="Remove assignment"
                >
                  <Trash2 className="size-4" />
                </button>
              </div>
            </div>
          );
        })}

        {addingAssignment && (
          <div className="rounded-lg border border-border bg-card px-4 py-3 space-y-3">
            <p className="text-sm font-medium">New assignment</p>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <label className="text-xs text-muted-foreground">
                  Customer code
                </label>
                <input
                  autoFocus
                  type="text"
                  className="w-full rounded-md border border-border bg-background px-3 py-1.5 text-sm uppercase"
                  placeholder="e.g. CUST01"
                  value={newCustomerCode}
                  onChange={(e) =>
                    setNewCustomerCode(e.target.value.toUpperCase())
                  }
                />
              </div>
              <div className="space-y-1">
                <label className="text-xs text-muted-foreground">
                  Price group
                </label>
                <select
                  className="w-full rounded-md border border-border bg-background px-3 py-1.5 text-sm"
                  value={newPriceGroupId}
                  onChange={(e) => setNewPriceGroupId(e.target.value)}
                >
                  <option value="">Select group…</option>
                  {nonDefaultGroups.map((g) => (
                    <option key={g.id} value={g.id}>
                      {g.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="flex gap-2 justify-end">
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  setAddingAssignment(false);
                  setNewCustomerCode("");
                  setNewPriceGroupId("");
                }}
              >
                Cancel
              </Button>
              <Button
                size="sm"
                disabled={
                  !newCustomerCode.trim() ||
                  !newPriceGroupId ||
                  savingAssignment
                }
                onClick={handleAddAssignment}
              >
                {savingAssignment ? "Saving…" : "Save"}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
